import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core';
import { compose, get, formatDate } from '../../utility';
import { withAppUser } from '../../shared/authorization/userConsumer';
import { ROLE_ID } from '../../constants';
import useRequest from '../../util/RequestEsign';
import { UnControlledTable } from '../../shared/table';
import styles from './superAdmin.styles';

function isSuperAdmin(user) {
  if (!user) return false;
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

const filterRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  marginBottom: 20,
  gap: 16,
  maxWidth: 720,
};

const dateValueTime = (v) => {
  if (v == null || v === '') return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const compareDateValuesAsc = (a, b) => dateValueTime(a) - dateValueTime(b);

const makeColumns = (history) => [
  { Header: 'User', accessor: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.email, id: 'user', sortable: true },
  { Header: 'Email', accessor: 'email', sortable: true },
  {
    Header: 'Amount',
    id: 'amount',
    accessor: (row) => row.amount || '',
    sortable: true,
    Cell: ({ value }) => (value ? String(value) : '—'),
  },
  {
    Header: 'Source',
    id: 'source',
    accessor: (row) => (row.source != null ? row.source : ''),
    sortable: true,
    Cell: ({ value }) => (value === '' || value === null || value === undefined ? '—' : String(value)),
  },
  {
    Header: 'Status',
    id: 'status',
    accessor: (row) => (row.status != null ? row.status : ''),
    sortable: true,
    Cell: ({ value }) => (value === '' || value === null || value === undefined ? '—' : String(value)),
  },
  {
    Header: 'Valid from',
    id: 'validFrom',
    accessor: (row) => row.validFrom || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Valid to',
    id: 'validTo',
    accessor: (row) => row.validTo || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Subscription created',
    id: 'subscriptionCreatedAt',
    accessor: (row) => row.subscriptionCreatedAt || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Last subscription update',
    id: 'subscriptionUpdatedAt',
    accessor: (row) => row.subscriptionUpdatedAt || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Account joined',
    id: 'accountCreatedAt',
    accessor: (row) => row.accountCreatedAt || '',
    sortable: true,
    sortMethod: compareDateValuesAsc,
    Cell: ({ value }) => (value ? formatDate(value) : '—'),
  },
  {
    Header: 'Agent',
    id: 'link',
    sortable: false,
    Cell: ({ original }) => (
      <a
        href={`#/admin/agent-management/${original.userId}`}
        onClick={(e) => { e.preventDefault(); history.push(`/admin/agent-management/${original.userId}`); }}
      >
        View agent
      </a>
    ),
  },
];

function SubscriptionActivityReportC({ classes, user, history }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortMode, setSortMode] = useState('payment');
  const [limit, setLimit] = useState('100');

  const fetchData = useCallback(async () => {
    if (!isSuperAdmin(user)) return;
    setLoading(true);
    try {
      let lim = parseInt(limit, 10);
      if (Number.isNaN(lim) || lim < 1) lim = 100;
      if (lim > 500) lim = 500;
      const qs = `?sort=${encodeURIComponent(sortMode)}&limit=${lim}`;
      const list = await useRequest({ endPoint: `/admin/reports/subscription-activity${qs}` });
      setData(Array.isArray(list) ? list : []);
    } catch {
      setData([]);
    }
    setLoading(false);
  }, [user, sortMode, limit]);

  useEffect(() => {
    if (!user) return;
    if (!isSuperAdmin(user)) {
      history.replace('/admin');
      return;
    }
    fetchData();
  }, [user, history, fetchData]);

  if (!user || !isSuperAdmin(user)) return null;

  const defaultSorted = sortMode === 'joined'
    ? [{ id: 'accountCreatedAt', desc: true }]
    : [{ id: 'subscriptionUpdatedAt', desc: true }];

  return (
    <div className={classes.root}>
      <Typography className={classes.title} variant="title">Subscription activity</Typography>
      <Typography color="textSecondary" style={{ marginBottom: 16, maxWidth: 720 }}>
        See recent subscription records: <strong>Last subscription update</strong> reflects the latest change to the subscription
        (e.g. renewals; used as a proxy when no separate payment ledger exists). Switch to <strong>Recently joined</strong> to sort
        by account creation date.
      </Typography>
      <div style={filterRowStyle}>
        <FormControl style={{ minWidth: 220 }}>
          <InputLabel htmlFor="subscription-activity-sort">Order by</InputLabel>
          <Select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            inputProps={{ id: 'subscription-activity-sort' }}
          >
            <MenuItem value="payment">Latest subscription activity</MenuItem>
            <MenuItem value="joined">Recently joined accounts</MenuItem>
          </Select>
        </FormControl>
        <TextField
          id="subscription-activity-limit"
          label="Rows (max 500)"
          type="number"
          value={limit}
          style={{ width: 140 }}
          onChange={(e) => setLimit(e.target.value)}
          inputProps={{ min: 1, max: 500 }}
        />
      </div>
      {loading ? (
        <Typography color="textSecondary">Loading…</Typography>
      ) : (
        <UnControlledTable
          key={sortMode}
          data={data}
          columns={makeColumns(history)}
          defaultSorted={defaultSorted}
        />
      )}
    </div>
  );
}

export default compose(
  withStyles(styles),
  withAppUser,
  withRouter
)(SubscriptionActivityReportC);
